import { STATUS, farbzustand } from '../koordination/gemeinsam';
import { formatiereZahl, type Fachstatus } from '../../muster';
import type { MsrArt, MsrAspArt, MsrGewerk, MsrHerkunft } from './typen';

/**
 * Was mehrere Reiter der MSR-Seite gemeinsam brauchen — reine Konstanten und
 * reine Funktionen, kein Zustand (Muster `koordination/gemeinsam.ts`).
 *
 * Die vier Statusfarben werden NICHT neu erfunden: `STATUS` aus
 * `koordination/gemeinsam.ts` liefert sie (grün/gelb/rot/grau), hier nur unter
 * den MSR-eigenen Namen verwendet — siehe `HERKUNFT_FARBE` und `EBENE_FARBE`.
 */

export const GEWERK: Record<MsrGewerk, string> = {
  rlt: 'Lüftung', ent: 'Entrauchung', hzg: 'Heizung', klt: 'Kälte',
  san: 'Sanitär/Trinkwasser', spr: 'Sprinkler', elt: 'Elektro',
  ra: 'Raumautomation',
};

export const ART: Record<MsrArt, string> = {
  BE: 'Binäreingang', BA: 'Binärausgang', AE: 'Analogeingang',
  AA: 'Analogausgang', ZE: 'Zähleingang', KOM: 'kommunikativ (Bus)',
};

export const ASP_ART: Record<MsrAspArt, string> = {
  lage: 'Lage (Quadrant.Geschoss)', gewerk: 'Gewerk', anlage: 'Anlage',
  entrauchung: 'Entrauchung',
};

export const HERKUNFT: Record<MsrHerkunft, string> = {
  abgeleitet: 'abgeleitet', geschaetzt: 'geschätzt', gepflegt: 'gepflegt',
  festlegung: 'Festlegung', vorlage: 'Vorlage', ki: 'KI',
};

/**
 * Die Farbe je Herkunft — aus den vier Statusfarben von `koordination`:
 * geschätzt ist die einzige, die eine Warnung verdient (sie ersetzt einen
 * fehlenden Messwert); alles andere ist neutral oder gut.
 */
export const HERKUNFT_FARBE: Record<MsrHerkunft, { farbe: string; text: string }> = {
  abgeleitet: { farbe: STATUS.ohne_anweisung.farbe, text: 'abgeleitet' },
  gepflegt: { farbe: STATUS.angenommen.farbe, text: 'gepflegt' },
  festlegung: { farbe: STATUS.angenommen.farbe, text: 'Festlegung' },
  geschaetzt: { farbe: STATUS.planer.farbe, text: 'geschätzt' },
  vorlage: { farbe: STATUS.ohne_anweisung.farbe, text: 'Vorlage' },
  ki: { farbe: STATUS.angenommen.farbe, text: 'KI' },
};

/**
 * Die Herkunft als Statusmarke (TM-8b, S1.5/S1.8): derselbe Zustand wie die Farbe aus `HERKUNFT_FARBE` - geschätzt
 * als Warnung -, aber über `StatusBadge` mit Text statt eines Farbkastens mit Farbliteral.
 */
export function herkunftMarke(h: MsrHerkunft): Fachstatus {
  const f = HERKUNFT_FARBE[h];
  return f ? { zustand: farbzustand(f.farbe), text: f.text } : { zustand: 'neutral', text: String(h) };
}

/** Eine Zahl deutsch formatiert (über `formatiereZahl`, S1.7) — leer heisst 0, nicht "undefined". */
export const z = (x: number | undefined | null) => formatiereZahl(x ?? 0);

/** Ein Prozentwert mit einer Nachkommastelle. */
export const zProzent = (x: number | undefined | null) => formatiereZahl(x ?? 0, 1);

/** Kürzelkatalog des AKS (Spezifikation 3.2) — statisch, ändert sich nicht je Projekt. */
export const AKS_KUERZEL: { kuerzel: string; bedeutung: string }[] = [
  { kuerzel: 'BM', bedeutung: 'Betriebsmeldung' },
  { kuerzel: 'SM', bedeutung: 'Störmeldung' },
  { kuerzel: 'FR', bedeutung: 'Freigabe/Befehl' },
  { kuerzel: 'AUF', bedeutung: 'Endlage Auf' },
  { kuerzel: 'ZU', bedeutung: 'Endlage Zu' },
  { kuerzel: 'RM', bedeutung: 'Rückmeldung' },
  { kuerzel: 'ST', bedeutung: 'Stellsignal' },
  { kuerzel: 'SW', bedeutung: 'Sollwert' },
  { kuerzel: 'DZ', bedeutung: 'Drehzahl' },
  { kuerzel: 'MW', bedeutung: 'Messwert' },
  { kuerzel: 'TF', bedeutung: 'Temperatur' },
  { kuerzel: 'DF', bedeutung: 'Druck' },
  { kuerzel: 'VS', bedeutung: 'Volumenstrom' },
  { kuerzel: 'FF', bedeutung: 'Feuchte' },
  { kuerzel: 'CO2', bedeutung: 'CO2-Gehalt' },
  { kuerzel: 'HS', bedeutung: 'Handschalter' },
  { kuerzel: 'FS', bedeutung: 'Frostschutz' },
  { kuerzel: 'FI', bedeutung: 'Filter' },
  { kuerzel: 'WZ', bedeutung: 'Zähler' },
];
